import React from 'react';
import {
  Box,
  Column,
  GridContainer,
  Row,
  Span,
  Typography,
  useScreenClass,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import PropTypes from 'prop-types';
import Date from '../../shared/Date';
import LineItemStatus from '../../shared/LineItemStatus';
import {
  mapItemStatesToStatuses,
  shouldShowCmrd,
} from '../../../util/mapStateToStatus';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { MemoizedItemPreviewDisplay } from '../../shared/itemPreviewDisplay/ItemPreviewDisplay';
import TrackingDisplayLink from '../../shared/trackingDisplay/TrackingDisplayLink';

/**
 * Expected that this is rendered within an LineItemDataProvider to access item properties
 *
 * Displays a slimmed down lineItem view.  This just shows the livePreview, productName, lineItemState, and promiseDate
 * @see LineItem for full lineItem display view with pricing and action buttons
 * @param item
 * @returns {JSX.Element}
 * @constructor
 */
const LineItemSlim = ({ showItemStatus = false }) => {
  const screenClass = useScreenClass();
  const [lineItemMessages] = useTranslations(['lineItemMessages']);
  const {
    trackingData: shipmentDetails,
    states: lineItemStates,
    deliverability,
    cmrd: promisedArrivalDate,
    livePreviewUrl,
    documentUrl: documentRefUrl,
    attributes: productAttributeList,
    productName,
    productVersion,
    productKey,
    quantity,
    id,
  } = useLineItemData();
  const { itemDeliverable } = deliverability;
  const lineItemStatuses = mapItemStatesToStatuses(
    lineItemStates,
    itemDeliverable
  );
  const hidePastCMRDs = false;
  const showCmrd = shouldShowCmrd(
    promisedArrivalDate,
    lineItemStatuses,
    hidePastCMRDs
  );

  return (
    <GridContainer>
      <Row>
        {screenClass !== 'xs' ? (
          <Column span={4} mb={3}>
            <MemoizedItemPreviewDisplay
              productAttributeList={productAttributeList}
              documentRefUrl={documentRefUrl}
              livePreviewUrl={livePreviewUrl}
              productKey={productKey}
              productVersion={productVersion}
              productName={productName}
            />
          </Column>
        ) : null}
        <Column span={8}>
          <Row>
            {screenClass === 'xs' ? (
              // span{4} was added because spans are required in Column component by SWAN v2
              // The following implementation should prorbably be revisited at some point.
              <Column span={4} spanXs={4} mb={3}>
                <MemoizedItemPreviewDisplay
                  productAttributeList={productAttributeList}
                  documentRefUrl={documentRefUrl}
                  livePreviewUrl={livePreviewUrl}
                  productKey={productKey}
                  productVersion={productVersion}
                  productName={productName}
                />
              </Column>
            ) : null}
            <Column span={12} spanXs={8}>
              {productName && (
                <Typography fontWeight="bold" fontSize="large">
                  {productName}
                </Typography>
              )}
              <LineItemStatus
                statuses={lineItemStatuses}
                disableItemStatus={!showItemStatus}
              />
              {quantity && (
                <Box component="p">
                  <Span textColor="subtle">{`${lineItemMessages.quantityField}: `}</Span>
                  <Span>{quantity}</Span>
                </Box>
              )}
              {shipmentDetails && (
                <TrackingDisplayLink
                  trackingData={shipmentDetails}
                  productName={productName}
                  itemId={id}
                />
              )}
              {showCmrd && (
                <Typography mt={3} textColor="subtle">
                  <ReplaceTokens
                    tokenizedString={lineItemMessages.expectedDeliveryMessage}
                    substitutions={{
                      date: <Date date={promisedArrivalDate} />,
                    }}
                  />
                </Typography>
              )}
            </Column>
          </Row>
        </Column>
      </Row>
    </GridContainer>
  );
};

export default LineItemSlim;

LineItemSlim.propTypes = {
  showItemStatus: PropTypes.bool,
};
